import { mapState } from 'vuex';
import { apiCallers } from '@/api/apiRequest';
import { required } from 'vuelidate/lib/validators';
import { downloadBlob, getFileNameFromHeader } from '@/utils/download';
import { formatMoney } from '@/modules/utils';

import NSelect from '@/components/common/n-select.vue';
import NText from '@/components/common/n-text.vue';
import ProgressIndicator from './progress-indicator';
import VkbBtn from '@/components/common/vkb-btn';

import ScrollHandler from '@/mixins/scrollHandler.js';
import ThemeHandler from '@/mixins/themeHandler.js';
import TrackingHandler from '@/mixins/trackingHandler.js';

export default {
  components: {
    NSelect,
    NText,
    ProgressIndicator,
    VkbBtn
  },

  mixins: [
    ScrollHandler,
    ThemeHandler,
    TrackingHandler
  ],

  data: () => ({
    form: {
      plz: '',
      ort: '',
      strasse: '',
      hausnummer: '',
    },

    loading: false,
    loading_gk: false,
    loading_pdf: false,
    search_started: false,

    orte: null,
    strassen: null,
    hausnummern: null,

    gk: null,
    gk_error: false,

    open_accordion: 0
  }),

  computed: {
    ...mapState([
      'appConfigs'
    ]),

    apiRequest () {
      return apiCallers[this.config.app];
    },

    config () {
      return this.appConfigs[this.$options.configIndex] || {};
    },

    errorMessages () {
      return {
        plz: {
          required: 'Bitte geben Sie Ihre PLZ ein',
          length: 'Bitte geben Sie die fünfstellige PLZ, ausschließlich bestehend aus Ziffern, ein.',
          range: this.config.theme === 'vkb'
            ? 'Lieber Interessent, der Risikocheck ist verfügbar für Postleitzahlen in Bayern und der Pfalz. Postleitzahlen außerhalb dieses Bereichs können wir leider nicht bearbeiten.'
            : (
              this.config.theme === 'fsoel'
                ? 'Lieber Interessent, der Risikocheck ist verfügbar für Postleitzahlen in Berlin und Brandenburg. Postleitzahlen außerhalb dieses Bereichs können wir leider nicht bearbeiten.'
                : 'Lieber Interessent, der Risikocheck ist verfügbar für Postleitzahlen im Saarland. Postleitzahlen außerhalb dieses Bereichs können wir leider nicht bearbeiten.'
            )
        },

        ort: {
          required: 'Bitte geben Sie den Ihre Ort ein.',
        },

        strasse: {
          required: 'Bitte geben Sie Ihre Straße ein.'
        },

        hausnummer: {
          required: 'Bitte geben Sie Ihre Hausnummer ein.'
        }
      };
    },

    isPlzValid() {
      return !!this.orte && this.orte.length > 0;
    },

    isFormValid() {
      return this.form.plz &&
        this.form.ort &&
        this.form.strasse &&
        this.form.hausnummer;
    },

    riskAccordions() {
      return [
        {
          id: 1,
          title: 'Überschwemmung',
          icon: 'fo-landwirtschaft-starkregen',
          icon_name: 'starkregen',
          progress1: this.gk ? this.gk.ueberschwemmung : 0,
          progress2: 5,
          descriptions: [
            'Die Hälfte aller Hochwasserschäden in Deutschland wird durch Starkregen verursacht.',
            'Wenn die Kanalisation bei extremen Regenfällen die Wassermassen nicht mehr vollständig aufnehmen kann, kann es zu Rückstauschäden wie überflutete Keller kommen.',
            'Laut einer FORSA-Umfrage von 2014 glauben fast drei Viertel der Deutschen, dass das eigene Haus nur schwer gegen Hochwasser versicherbar ist. Tatsächlich sind etwa 99% der Gebäude in Deutschland problemlos versicherbar.'
          ],
          tip: 'Mehr Informationen zu individuellen Hochwasser-Schutzmaßnahmen finden Sie hier: ' +
            '<a href="https://www.hochwasser-pass.info" target="_blank">https://www.hochwasser-pass.info</a>'
        },
        {
          id: 2,
          title: 'Sturm und Hagel',
          icon: 'fo-qohngebaeudeversicherung-sturm-hagel',
          icon_name: 'sturm',
          progress1: this.gk ? this.gk.sturm : 0,
          progress2: 2,
          descriptions: [
            '<b style="padding: 0;">Stürme</b> mit verheerenden Folgen treten auch in Deutschland immer häufiger auf. Klimaforscher gehen davon aus, dass Stürmschäden bis zum Jahr 2100 um biszu 50% steigen könnnen.'
          ],
          tip: 'Bäume in der Nähe Ihres Hauses können bei einem Sturm schwere Schäden anrichten. Achtung: Auch einen kranken Baum dürfen Sie nicht einfach abholzen, weil in Ihrer Kommune eine Baumschutzordnung bestehen kann.',
          hagel_descriptions: [
            '<b style="padding: 0;">Hagel</b> wird als Risiko oftmals unterschätzt, hinterlässt häufig aber bleibende und teure Schäden.',
            'In den letzten 30 Jahren sind Hagelschäden um das 7-fache gestiegen.'
          ],
          hagel_tip: 'Falls Sie gerade bauen, achten Sie auf hagelresistente Materialien (z.B. hagelresistentes Glas. Außerdem schützen Dachüberstände die Fassade.'
        },
        {
          id: 3,
          title: 'Feuer',
          icon: 'fo-feuer',
          icon_name: 'feuer',
          progress1: 2,
          progress2: 5,
          descriptions: [
            'Zwar sind Feuerschäden relativ selten, die Wahrscheinlichkeit eines völligen Verlustes aber umso höher. Auslöser sind oft defekte Elektrik und Fahrlässigkeit.'
          ],
          tip: 'Denken Sie daran, in Ihren Räumen Rauchmelder anzubringen.<br>In ganz Deutschland ist der Einbau von Rauchmeldern in Neu- und Umbauten Pflicht. Für Bestandsbauten gilt in beinahe allen Bundesländern eine Rauchmelderpflicht.'
        },
        {
          id: 4,
          title: 'Überspannung durch Blitz',
          icon: 'fo-blitzschlag',
          icon_name: 'blitzschlag',
          progress1: 3,
          progress2: 1,
          descriptions: [
            'Überspannungsschäden sind eine häufige Folge von Blitzeinschlägen. Das betrifft vor allem empfindliche und teure Elektrogeräte.',
            'Die Zahl der Blitzschäden ist zurückgegangen, die Höhe des Einzelschadens dagegen gestiegen.'
          ],
          tip: 'Gerade für freistehende Gebäude ist eine Absicherung mit einem Blitzableiter wichtig.'
        },
        {
          id: 5,
          title: 'Leitungswasser',
          icon: 'fo-leitungswasser',
          icon_name: 'leitungswasser',
          progress1: 5,
          progress2: 3,
          descriptions: [
            'Zu den häufigsten Schäden im Haus gehören Rohrbrüche und Nässeschäden (wie ein geplatzter Schlauch der Spülmaschine).',
            'Und: Je älter das Gebäude, desto höher ist das Risiko für einen Leitungswasserschaden.'
          ],
          tip: 'Vereiste Wasserleitungen können große Schäden anrichten. Achten Sie deshalb auch bei Abwesenheit darauf, dass die Temperaturen nicht unter Null fallen. '
        }
      ];
    },
  },

  mounted() {
    // setTimeout(() => {
    //   this.focusInput();
    // }, 500);
    this.triggerTransitionEvent('interaktivesTool', 1, 'risikoCheck', -1, '-');
  },

  methods: {
    focusInput() {
      const plzInput = document.getElementById('risiko-plz');

      if (plzInput) {
        plzInput.focus();
      }
    },

    formatMoney(value) {
      return `${ formatMoney(value) } €`;
    },

    getErrorMessage(field = null) {
      const validity = field
        ? this.$v.form[field]
        : this.$v.form;

      if (!validity || !validity.$dirty) {
        return;
      }

      const error_key = Object.keys(validity.$params).find(k => validity[k] === false);

      return error_key
        ? this.errorMessages[field][error_key]
        : '';
    },

    onNewSearch() {
      this.resetForm();
      this.focusInput();

      this.loading = false;
      this.loading_gk = false;
      this.loading_pdf = false;
      this.search_started = false;
    },

    onSearchAddress(type) {
      this.resetForm(type);
      this.$v.form.$touch();
      this.triggerValidationEvent(this.$v.form, [ type ], this.errorMessages);

      if (this.$v.form.$invalid) {
        return;
      }

      const moduleSubStepId = {
        plz: 1,
        ort: 2,
        strasse: 3,
      };

      this.triggerTransitionEvent('interaktivesTool', 1, 'risikoCheck', moduleSubStepId[type], type);

      const params = {
        plz: this.form.plz,
        ort: this.form.ort,
        strasse: this.form.strasse
      };

      this.loading = true;
      this.search_started = true;

      this.apiRequest
        .get(this.config.paths.address, { params })
        .then(res => {
          if (!this.loading) {
            return;
          }

          if (type === 'strasse') {
            this.hausnummern = res.data.data || [];
          } else if (type === 'ort') {
            this.strassen = res.data.data || [];
          } else {
            this.orte = res.data.data || [];
          }

          if (type === 'plz' && !this.orte.length) {
            this.triggerValidationEvent(this.$v.form, ['plz'], this.errorMessages);
          }
        })
        .catch(() => {
          if (type === 'strasse') {
            this.form.strasse = null;
          } else if (type === 'ort') {
            this.form.ort = null;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onCalculate() {
      this.$v.form.$touch();
      this.triggerValidationEvent(this.$v.form, ['plz', 'ort', 'strasse', 'hausnummer'], this.errorMessages);

      if (this.$v.form.$invalid) {
        return;
      }

      this.triggerTransitionEvent('interaktivesTool', 2, 'ergebnisRisikoCheck', -1, '-');

      const params = {
        plz: this.form.plz,
        ort: this.form.ort,
        strasse: this.form.strasse,
        hausnummer: this.form.hausnummer
      };

      this.loading_gk = true;

      this.apiRequest
        .get(this.config.paths.gk, { params })
        .then(res => {
          if (!this.loading_gk) {
            return;
          }
          this.gk = res.data;
          this.loading_gk = false;
          this.scrollToElement('calc-result', 0);
        })
        .catch(() => {
          this.form.hausnummer = null;
          this.loading_gk = false;
        });
    },

    onContactClick() {
      if (this.config.contact_url) {
        window.open(this.config.contact_url, '_top');
        return;
      }

      const contact_urls = {
        vkb: 'https://www.vkb.de/content/versicherungen/haus-wohnen/wohngebaeudeversicherung/beraten-anfragen/',
        fsoel: 'https://www.feuersozietaet.de/content/versicherungen/haus-wohnen/wohngebaeudeversicherung/beraten-anfragen/',
        slv: 'https://www.saarland-versicherungen.de/content/service/kontakt-aufnehmen/e-mail-anfrage/?id=anfrage'
      };

      window.open(contact_urls[this.config.theme], '_blank');
    },

    onDownloadPdf() {
      const params = {
        plz: this.form.plz,
        ort: this.form.ort,
        strasse: this.form.strasse,
        hausnummer: this.form.hausnummer,
        ueberschwemmung: this.gk.ueberschwemmung,
        sturm: this.gk.sturm,
        theme: this.config.theme
      };

      this.loading_pdf = true;

      this.apiRequest
        .get(this.config.paths.pdf, {
          params,
          responseType: 'blob'
        })
        .then(res => {
          downloadBlob(res.data, getFileNameFromHeader(res.headers));
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading_pdf = false;
        });
    },

    onOpenAccordion(id) {
      this.open_accordion = this.open_accordion !== id
        ? id
        : 0;
    },

    resetForm(type = 'all') {
      this.form.hausnummer = '';

      if (type === 'strasse') {
        this.hausnummern = this.hausnummern ? [] : null;

      } else if (type === 'ort') {
        this.form.strasse = '';

        this.hausnummern = this.hausnummern ? [] : null;
        this.strassen = this.strassen ? [] : null;

      } else if (type === 'all') {
        this.form.plz = '';
        this.form.ort = '';
        this.form.strasse = '';

        this.orte = null;
        this.strassen = null;
        this.hausnummern = null;
      }

      this.gk = null;
      this.$v.$reset();
    }
  },

  validations: {
    form: {
      plz: {
        required,
        length: (v) => !v || v.length === 5,
        range() {
          return !this.form.plz || !this.orte || this.orte.length > 0;
        }
      },

      ort: {
        required(value) {
          return !this.orte || !this.orte.length || !!value;
        }
      },

      strasse: {
        required(value) {
          return !this.strassen || !this.strassen.length || !!value;
        }
      },

      hausnummer: {
        required(value) {
          return !this.hausnummern || !this.hausnummern.length || !!value;
        }
      }
    }
  },

  watch: {
    'form.plz': {
      handler() {
        this.orte = null;
        if (!this.form.plz || this.form.plz.toString().length !== 5) {
          return;
        }
        this.onSearchAddress('plz');
      }
    },

    'form.hausnummer': {
      handler() {
        this.gk = null;
        this.triggerTransitionEvent('interaktivesTool', 1, 'risikoCheck', 4, 'hausnummer');
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/apps/risiko_check/logic.js